import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import SectionStyles from "@/components/sections/SectionStyles";
import { useSite } from "@/contexts/SiteContext";

// Fallback self-hosted (data URI) pentru cursurile fără copertă — fără dependență externă.
const COVER_FALLBACK =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='750'%3E%3Crect width='1200' height='750' fill='%230D1B2A'/%3E%3Ctext x='600' y='388' fill='%23B49054' font-family='Georgia,serif' font-size='56' text-anchor='middle'%3ECurs%3C/text%3E%3C/svg%3E";

export default function Courses() {
  const { site, sectionContent } = useSite();
  const c = sectionContent("courses_hero") || {};
  const showPrices = site?.show_prices !== false;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/public/courses").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <div data-testid="courses-page">
      <SectionStyles scopeKey="courses_hero" content={c} />
      <section data-section-key="courses_hero" data-section-self data-section-title="Cursuri — antet" className="py-12 lg:py-20" style={{ background: "var(--ct-bg)" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="mb-6"><Breadcrumbs items={[{ label: "Cursuri" }]} /></div>
          <div className="max-w-3xl mb-12">
            <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Cursuri online"}</span>
            <h1 data-el="title" data-field="title" className="font-serif text-5xl sm:text-6xl mt-4 leading-tight">{c.title || "Programe structurate de transformare interioară"}</h1>
            <p data-el="body" data-field="body" className="text-base leading-relaxed mt-5" style={{ color: "var(--ct-text-muted)" }}>
              {c.body || "Cursuri concepute pentru oameni care vor să meargă în profunzime, la rădăcina blocajului, și să iasă cu un răspuns interior nou."}
            </p>
          </div>

          {loading ? (
            <p style={{ color: "var(--ct-text-muted)" }}>Se încarcă...</p>
          ) : items.length === 0 ? (
            <div className="card-premium p-10 text-center" data-testid="courses-empty">
              <p style={{ color: "var(--ct-text-muted)" }}>Cursurile vor fi publicate în curând.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((course, i) => (
                <Link
                  key={course.id}
                  to={`/cursuri/${course.slug}`}
                  className="card-premium overflow-hidden flex flex-col"
                  data-testid={`courses-card-${i}`}
                >
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img
                      src={course.cover_image || COVER_FALLBACK}
                      alt={course.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                    {course.is_free && (
                      <span
                        className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-medium"
                        style={{ background: "var(--ct-emerald)", color: "var(--ct-gold)", border: "1px solid var(--ct-gold)" }}
                        data-testid={`badge-free-${course.slug}`}
                      >
                        Gratuit
                      </span>
                    )}
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-serif text-2xl mb-2 leading-tight">{course.title}</h3>
                    <p className="text-sm leading-relaxed flex-1" style={{ color: "var(--ct-text-muted)" }}>{course.subtitle}</p>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--ct-gold)" }}>
                        Vezi cursul <ChevronRight size={14} />
                      </span>
                      {showPrices && !course.is_free && course.price > 0 && (
                        <span className="text-sm font-medium" style={{ color: "var(--ct-link)" }}>{course.price} {course.currency || "RON"}</span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
